import { config } from 'affiny-core/env';
import { EMemberChoicesLabels } from '../../models/memberchoice/memberchoice.types';
import {
  IAnswers,
  IApiReflist,
  IReflist,
} from '../../models/reflist/reflist.types';
import {
  EQuestionType,
  EReflistChapterName,
  IDryReflist,
  IDryReflistQuestion,
  IHydratedReflist,
  TReflistQuestion,
} from './reflists.types';

const SEARCH_MOBILE_QUESTIONS_MAPPING = [
  2,
  2,
  2,
  1,
  1,
  1,
  1,
  2,
  2,
  2,
  2,
  2,
  2,
  1,
  1,
];
const SEARCH_DESKTOP_QUESTIONS_MAPPING = [
  1,
  2,
  2,
  2,
  1,
  1,
  1,
  2,
  2,
  2,
  2,
  2,
  2,
  1,
  1,
];

const getFlatObjectMappingSearchQuestions: (
  isWam: boolean
) => IDryReflistQuestion[] = (isWam: boolean) => [
  {
    type: EQuestionType.BULLET,
    label: EMemberChoicesLabels.selftestDirectSubtle,
  },
  {
    type: EQuestionType.BULLET,
    label: EMemberChoicesLabels.selftestConfidentShy,
  },
  {
    type: EQuestionType.BULLET,
    label: EMemberChoicesLabels.selftestInstinctiveReflective,
  },
  {
    type: EQuestionType.BULLET,
    label: EMemberChoicesLabels.selftestHomebirdParty,
  },
  {
    type: EQuestionType.BULLET,
    label: EMemberChoicesLabels.selftestMethodicalSpontaneous,
  },
  {
    type: EQuestionType.BULLET,
    label: EMemberChoicesLabels.selftestDreamerRational,
  },
  {
    type: EQuestionType.BULLET,
    label: EMemberChoicesLabels.selftestCalmEnergetic,
  },
  { type: EQuestionType.LIST, label: EMemberChoicesLabels.sociotestLook },
  {
    type: EQuestionType.RANGE,
    label: EMemberChoicesLabels.searchAge,
    initialValues: [30, 50],
    stepValues: [20, 30, 40, 50, 60, 70, 80, 90, 100],
    unit: 'profileCapture.units.years',
    minDifference: 4,
    withValidation: isWam,
  },
  {
    type: EQuestionType.RANGE,
    label: EMemberChoicesLabels.searchHeight,
    initialValues: [150, 180],
    stepValues: [140, 150, 160, 170, 180, 190, 200],
    unit: 'profileCapture.units.height',
    minDifference: 4,
    withValidation: isWam,
  },
  { type: EQuestionType.LIST, label: EMemberChoicesLabels.searchStudies },
  {
    type: EQuestionType.INFLUENCE,
    label: EMemberChoicesLabels.searchStudiesWeighting,
  },
  { type: EQuestionType.LIST, label: EMemberChoicesLabels.childsAtHome },
  {
    type: EQuestionType.INFLUENCE,
    label: EMemberChoicesLabels.childsAtHomeWeighting,
  },
  {
    type: EQuestionType.MULTIPLE,
    label: EMemberChoicesLabels.searchEthnicity,
    maxChoice: 3,
  },
  {
    type: EQuestionType.INFLUENCE,
    label: EMemberChoicesLabels.searchEthnicityWeighting,
    withValidation: false,
  },
  {
    type: EQuestionType.MULTIPLE,
    label: EMemberChoicesLabels.searchReligion,
    maxChoice: 3,
  },
  {
    type: EQuestionType.INFLUENCE,
    label: EMemberChoicesLabels.searchReligionWeighting,
    withValidation: false,
  },
  {
    type: EQuestionType.LIST,
    label: EMemberChoicesLabels.searchSmokeHabit,
  },
  {
    type: EQuestionType.INFLUENCE,
    label: EMemberChoicesLabels.searchSmokeHabitWeighting,
  },
  {
    type: EQuestionType.BULLET,
    label: EMemberChoicesLabels.selftestPartnerPersonality,
  },
  {
    type: EQuestionType.BULLET,
    label: EMemberChoicesLabels.selftestProblemAdvice,
  },
  {
    type: EQuestionType.BULLET,
    label: EMemberChoicesLabels.selftestAccomodatingOppose,
  },
  {
    type: EQuestionType.LIST,
    label: EMemberChoicesLabels.sociotestBodyPartAttraction,
  },
];

const getObjectMappingSearchQuestions = (isWam: boolean) => {
  let position = 0;

  return (isWam
    ? SEARCH_MOBILE_QUESTIONS_MAPPING
    : SEARCH_DESKTOP_QUESTIONS_MAPPING
  ).map(numberOfQuestions =>
    getFlatObjectMappingSearchQuestions(isWam).slice(
      position,
      (position += numberOfQuestions)
    )
  );
};

export const getObjectMappingQuestions: (
  isWam: boolean
) => IDryReflist = isWam => ({
  [EReflistChapterName.SEARCH]: getObjectMappingSearchQuestions(isWam),
  [EReflistChapterName.VISION]: [
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestLongTermProjects,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestSharedDecisionsMaking,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestSharedCulturalReferences,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestSpecificRoles,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestRomanticGestures,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestCommonIntrests,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestBeingTheOne,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestPartnerSupport,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestAboutLove,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestHarmoniousRelationship,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestPersonalTimeOut,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.sociotestReceptionCooking,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestLoveVision,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestCoupleVision,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestThoughtSharing,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestRelationshipSexuality,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestSexDrive,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestSexLife,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestRelaxingEvening,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestBestFriendIssue,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestPartnerCouncil,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestPartnerClothingTaste,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestPartnerHabits,
      },
    ],
  ],
  [EReflistChapterName.PERSONALITY]: [
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.selftestPersonality,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestPersonalAttitude,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestEfficientRelaxed,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestExtrovertIntrovert,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestSaverSpender,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestOrganized,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestLifeMindset,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestFutureVision,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestWorrying,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestFirstSight,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestBeingHappy,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.sociotestStressManagment,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestSocietyBehaviour,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestTalkInvolvement,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestContradictBehaviour,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestFriendsLikes,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestFriendsAppreciate,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestDecisionMaking,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestCompulsiveShopping,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestFreeTime,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestApproachStranger,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestPromotionManagement,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.sociotestQualityTime,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestFeelingFree,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestAdaptChallenges,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestEnjoysLife,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestRiskManagement,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestOtherOpinions,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestRelyOnExperience,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestTravellingExploring,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestCarePeople,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestAchieveGoals,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestPartyOrganizer,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestDecisionUnderInfluence,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestAdaptChange,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestLiveEnjoyMyself,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestTakingRisks,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestDislikeChange,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestRespectValues,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestAltruisticness,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestHoldGrudges,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestWorkLifeBalance,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestInControl,
      },
    ],
  ],
  [EReflistChapterName.TASTES]: [
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.sociotestLivingIn,
        hasVisuals: true,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.sociotestMakeMeHappy,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.sociotestLuxuryDefinition,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.sociotestLittleWeakness,
      },
    ],
    [
      {
        type: EQuestionType.MULTIPLE,
        maxChoice: 3,
        label: EMemberChoicesLabels.selftestHolidayDestination,
        withValidation: true,
        hasVisuals: true,
      },
    ],
    [
      {
        type: EQuestionType.MULTIPLE,
        maxChoice: 3,
        label: EMemberChoicesLabels.profileHobbies,
        withValidation: true,
      },
    ],
    [
      {
        type: EQuestionType.MULTIPLE,
        maxChoice: 3,
        label: EMemberChoicesLabels.profilePet,
        withValidation: true,
      },
    ],
    [
      {
        type: EQuestionType.MULTIPLE,
        maxChoice: 3,
        label: EMemberChoicesLabels.profileLeisure,
        withValidation: true,
      },
    ],
    [
      {
        type: EQuestionType.MULTIPLE,
        maxChoice: 3,
        label: EMemberChoicesLabels.profileSports,
        withValidation: true,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.selftestArtisticStyle,
        hasVisuals: true,
      },
    ],
    [
      {
        type: EQuestionType.MULTIPLE,
        maxChoice: 3,
        label: EMemberChoicesLabels.sociotestMusicStyle,
        withValidation: true,
      },
    ],
    [
      {
        type: EQuestionType.MULTIPLE,
        maxChoice: 3,
        label: EMemberChoicesLabels.sociotestFilm,
        withValidation: true,
      },
    ],
  ],
  [EReflistChapterName.VALUES]: [
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestLongTermRelationship,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestFidelity,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestFamilyTies,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestFamilyRelationship,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestParentChildRelationship,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestChildrenEducation,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestFamilyLife,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestChildrenBalancedDiet,
      },
    ],
    [
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestMoneyImportance,
      },
      {
        type: EQuestionType.BULLET,
        label: EMemberChoicesLabels.selftestSpirituality,
      },
    ],
  ],
  [EReflistChapterName.PERSONAL_DATA]: [
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.profileReligion,
        notice: EMemberChoicesLabels.profileReligionNotice,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.profileReligionBehaviour,
        notice: EMemberChoicesLabels.profileReligionBehaviourNotice,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.profileFoodHabit,
      },
    ],
    [{ type: EQuestionType.LIST, label: EMemberChoicesLabels.alcoholHabit }],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.profileSmokeHabit,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.profileEthnicity,
        notice: EMemberChoicesLabels.profileEthnicityNotice,
      },
    ],
    [
      {
        type: EQuestionType.SEARCH,
        label: EMemberChoicesLabels.profileNationality,
        defaultAnswers: () => config.env.defaultNationalityIds,
      },
    ],
    [
      {
        type: EQuestionType.SEARCH,
        label: EMemberChoicesLabels.profileJob,
        questionId: 57,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.profileMaritalStatus,
      },
    ],
    [{ type: EQuestionType.LIST, label: EMemberChoicesLabels.childNumber }],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.profileChildrenWish,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.profileStudies,
      },
    ],
    [{ type: EQuestionType.LIST, label: EMemberChoicesLabels.profileIncome }],
    [
      {
        type: EQuestionType.SLIDER,
        label: EMemberChoicesLabels.profileHeight,
        unit: 'profileCapture.units.height',
        stepValues: [140, 150, 160, 170, 180, 190, 200],
        initialValue: 170,
        withValidation: isWam,
      },
    ],
    [
      {
        type: EQuestionType.SLIDER,
        label: EMemberChoicesLabels.profileWeight,
        unit: 'profileCapture.units.weight',
        stepValues: [40, 50, 60, 70, 80, 90, 100, 110, 120],
        initialValue: 80,
        withValidation: isWam,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.profileBodyShape,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.profileHairColor,
      },
    ],
    [
      {
        type: EQuestionType.LIST,
        label: EMemberChoicesLabels.profileHairStyle,
      },
    ],
    [{ type: EQuestionType.LIST, label: EMemberChoicesLabels.profileEyes }],
  ],
});

export const reflistsMapper = (
  reflists: IApiReflist,
  dryReflists: IDryReflist
) => ({
  me: parseReflists(reflists.attributes.me, dryReflists),
  other: parseReflists(reflists.attributes.other, dryReflists),
});

const parseReflists = (
  reflistQuestions: IReflist,
  dryReflists: IDryReflist
) => {
  return Object.entries<IDryReflistQuestion[][]>(
    dryReflists
  ).reduce<IHydratedReflist>((results, [key, multiple]) => {
    results[key] = {
      questions: multiple.map(questions =>
        questions.map<TReflistQuestion>(question => {
          const answers: IAnswers = reflistQuestions[question.label];

          return {
            ...invokeFnProps(question),
            title: `profileCapture.reflist.${question.label}`,
            subtitle: question.notice
              ? `profileCapture.reflist.${question.notice}`
              : undefined,
            answers:
              answers && answers.labels
                ? answers.labels.map(answer => ({
                    ...answer,
                    label: answer.label
                      .replace(/\ \!/g, '\u00a0!')
                      .replace(/\ \?/g, '\u00a0?')
                      .replace(/\ \:/g, '\u00a0:'),
                  }))
                : undefined,
          } as TReflistQuestion;
        })
      ),
    };

    return results;
  }, {});
};

const invokeFnProps = (question: IDryReflistQuestion) => {
  const questionClone = { ...question };

  for (const prop in question) {
    if (typeof question[prop] === 'function') {
      questionClone[prop] = question[prop]();
    }
  }

  return questionClone;
};
